import { setSadCookie } from "../../../../utils/cookies";
import { pushToDataLayer } from "../../../../utils/google";
import { generateUUID } from "../../../../utils/uuid";
import { getLocalAccountProfile, handleMergeOfProfiles } from "./Account.utils";
import { getOfflineProfile } from "./Offline.utils";

/**
 * Retrieves the initial profile for the user.
 *
 * This function first attempts to get the account profile from local storage.
 * If an account profile exists, it returns that profile.
 * Otherwise, it returns an offline profile.
 *
 * @returns {DogsTrust.Profile} The user's profile data
 */
export const getInitialProfile = (): DogsTrust.Profile => {
	const accountProfile = getLocalAccountProfile();

	if (accountProfile) {
		console.log(
			"🚀 getInitialProfile | Account profile found in local storage:",
			accountProfile,
		);
		return handleMergeOfProfiles(accountProfile);
	}

	console.log(
		"🚀 getInitialProfile | No account profile found, using offline profile.",
	);
	return getOfflineProfile();
};

/**
 * Adds a dog to the user's sponsorship basket
 *
 * @param dog - The dog sponsorship information to add to the basket
 * @param currentProfile - The current user profile
 * @returns Updated user profile with the dog added to the basket
 */
export function addToBasket(
	dog: DogsTrust.SADCookie,
	currentProfile: DogsTrust.Profile,
): DogsTrust.Profile {
	// Track the addition in the data layer
	pushToDataLayer({
		event: "addToCart",
		ecommerce: {
			currencyCode: "GBP",
			add: {
				products: [
					{
						name: `Sponsorship - ${dog.name}`,
						id: dog.fundCode,
						price: dog.amount,
						brand: "Sponsorship",
						category: "",
						variant: "",
						quantity: 1,
					},
				],
			},
		},
	});

	// Add dog to basket with unique ID
	const updatedProfile = {
		...currentProfile,
		basket: [
			...(currentProfile.basket || []),
			{ ...dog, basketId: generateUUID() },
		],
	};

	setSadCookie(updatedProfile.basket, updatedProfile.amount);

	return updatedProfile;
}

/**
 * Removes a dog from the user's sponsorship basket
 *
 * @param dog - The dog sponsorship information to remove from the basket
 * @param currentProfile - The current user profile
 * @param indexToRemove - Optional specific index of the dog to remove
 * @returns Updated user profile with the dog removed from the basket
 */
export function removeFromBasket(
	dog: DogsTrust.SADCookie,
	currentProfile: DogsTrust.Profile,
	indexToRemove?: number,
): DogsTrust.Profile {
	// Track the removal in the data layer
	pushToDataLayer({
		event: "removeFromCart",
		ecommerce: {
			currencyCode: "GBP",
			remove: {
				products: [
					{
						name: `Sponsorship - ${dog.name}`,
						id: dog.fundCode,
						brand: "Sponsorship",
						gift: dog.isGift ? "yes" : "no",
						category: "",
						variant: "",
						quantity: 1,
					},
				],
			},
		},
	});

	let updatedBasket: DogsTrust.SADCookie[];

	if (indexToRemove === undefined) {
		// Find the last occurrence if no specific index provided
		const indexOfDog = currentProfile.basket.findIndex(
			(basketDog) => basketDog.fundCode === dog.fundCode,
		);
		updatedBasket = currentProfile.basket.filter((_, i) => i !== indexOfDog);
	} else {
		// Remove at the specific index
		updatedBasket = currentProfile.basket.filter((_, i) => i !== indexToRemove);
	}

	const updatedProfile = {
		...currentProfile,
		basket: updatedBasket,
	};

	setSadCookie(updatedProfile.basket, updatedProfile.amount);

	return updatedProfile;
}

/**
 * Adds a dog to the user's favorites list
 *
 * @param dog - The dog information to add to favorites
 * @param data - Tracking data for analytics
 * @param currentProfile - The current user profile
 * @returns Updated user profile with the dog added to favorites
 */
export function addToFavourites(
	dog: DogsTrust.DogData,
	data: DogsTrust.DogDataTracking,
	currentProfile: DogsTrust.Profile,
): DogsTrust.Profile {
	// Check if dog is already in favourites
	const dogIsAlreadyInFavourites = currentProfile.favourites?.some(
		(favDog) => favDog?.apiKey === dog?.apiKey,
	);

	// If already in favorites, return unchanged profile
	if (dogIsAlreadyInFavourites) {
		return currentProfile;
	}

	// Handle tracking
	const currentPageUrl =
		typeof window !== "undefined" ? window.location.pathname : "";
	pushToDataLayer({
		...data,
		card_location: currentPageUrl,
		card_title: dog.name,
		card_sub_title: dog.breed,
		card_state: "added",
	} as Google.DataLayerObject);

	// Return updated profile with new favorite
	return {
		...currentProfile,
		favourites: [...currentProfile.favourites, dog],
	};
}

/**
 * Removes a dog from the user's favorites list
 *
 * @param dog - The dog information to remove from favorites
 * @param data - Tracking data for analytics
 * @param currentProfile - The current user profile
 * @returns Updated user profile with the dog removed from favorites
 */
export function removeFromFavourites(
	dog: DogsTrust.DogData,
	data: DogsTrust.DogDataTracking,
	currentProfile: DogsTrust.Profile,
): DogsTrust.Profile {
	// Handle tracking
	const currentPageUrl =
		typeof window !== "undefined" ? window.location.pathname : "";
	pushToDataLayer({
		...data,
		card_location: currentPageUrl,
		card_title: dog.name,
		card_sub_title: dog.breed,
		card_state: "removed",
	} as Google.DataLayerObject);

	// Return updated profile without the dog
	return {
		...currentProfile,
		favourites: currentProfile.favourites.filter(
			(favDog) => favDog?.apiKey !== dog?.apiKey,
		),
	};
}

/**
 * Adds a dog to the user's interested list
 *
 * @param dog - The dog information to add to interested list
 * @param currentProfile - The current user profile
 * @returns Updated user profile with the dog added to interested list
 */
export function addToInterested(
	dog: DogsTrust.DogData,
	currentProfile: DogsTrust.Profile,
): DogsTrust.Profile {
	// Check if dog is already in interested list
	const dogAlreadyInterested = currentProfile.interested.some(
		(interestedDog) => interestedDog?.apiKey === dog?.apiKey,
	);

	// If already in interested, return unchanged profile
	if (dogAlreadyInterested) {
		return currentProfile;
	}

	// Return updated profile with new interested dog
	return {
		...currentProfile,
		interested: [...currentProfile.interested, dog],
	};
}

/**
 * Removes a dog from the user's interested list
 *
 * @param dog - The dog information to remove from interested list
 * @param currentProfile - The current user profile
 * @returns Updated user profile with the dog removed from interested list
 */
export function removeFromInterested(
	dog: DogsTrust.DogData,
	currentProfile: DogsTrust.Profile,
): DogsTrust.Profile {
	// Return updated profile without the dog
	return {
		...currentProfile,
		interested: currentProfile.interested.filter(
			(interestedDog) => interestedDog?.apiKey !== dog?.apiKey,
		),
	};
}

/**
 * Updates whether a dog in the basket is marked as a gift
 *
 * @param basketKey - The index of the dog in the basket
 * @param isGift - Whether the sponsorship is a gift or not
 * @param currentProfile - The current user profile
 * @returns Updated user profile with the gift status updated
 */
export function updateRecipient(
	basketKey: number,
	isGift: boolean,
	currentProfile: DogsTrust.Profile,
): DogsTrust.Profile {
	const updatedProfile = {
		...currentProfile,
		basket: currentProfile.basket.map((dog, i) => ({
			...dog,
			isGift: i === basketKey ? isGift : dog.isGift,
		})),
	};

	setSadCookie(updatedProfile.basket, updatedProfile.amount);

	return updatedProfile;
}

/**
 * Clears all items from the user's basket
 *
 * @param currentProfile - The current user profile
 * @returns Updated user profile with an empty basket
 */
export function clearBasket(
	currentProfile: DogsTrust.Profile,
): DogsTrust.Profile {
	return {
		...currentProfile,
		basket: [],
	};
}

/**
 * Updates the donation amount in the user's profile
 *
 * @param amount - The new donation amount
 * @param currentProfile - The current user profile
 * @returns Updated user profile with the new amount set
 */
export function updateAmount(
	amount: number,
	currentProfile: DogsTrust.Profile,
): DogsTrust.Profile {
	const updatedProfile = {
		...currentProfile,
		amount,
	};

	setSadCookie(currentProfile.basket, amount);

	return updatedProfile;
}

/**
 * Evaluates whether a user is eligible to submit an adoption form
 *
 * Rules differ based on environment:
 * - In production: Users can only submit an application once every 3 months
 * - In other environments: Users can submit once every 10 minutes (for testing)
 *
 * @param adoptionSubmissions - Array of timestamps of previous adoption submissions
 * @returns Boolean indicating if the user can submit a new adoption form
 */
export function evaluateIfUserCanSubmitAdoptionForm(
	adoptionSubmissions: DogsTrust.Profile["adoptionSubmissions"],
): boolean {
	if (!adoptionSubmissions || adoptionSubmissions.length === 0) {
		return true;
	}

	if (process.env.GATSBY_ENVIRONMENT === "prod") {
		// They can only submit an application once every 3 months
		const threeMonthsAgo = new Date();
		threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
		const submissionsWithinLastThreeMonths = adoptionSubmissions.filter(
			(submission) => submission > threeMonthsAgo.getTime(),
		);
		const canSubmit = submissionsWithinLastThreeMonths.length === 0;

		return canSubmit;
	}

	// For non-prod environments, limit to one submission every 10 minutes
	const tenMinsAgo = new Date();
	tenMinsAgo.setMinutes(tenMinsAgo.getMinutes() - 10);
	const submissionsWithinLastTenMins = adoptionSubmissions.filter(
		(submission) => submission > tenMinsAgo.getTime(),
	);
	const canSubmit = submissionsWithinLastTenMins.length === 0;

	return canSubmit;
}
